import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CheckBoxOutlineBlank,
  Close,
  DeleteOutline,
  DragIndicator,
  DriveFileMove,
  Folder,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import {
  ApiBusiness,
  ApiFolder,
  fetchBusinessesFromFolder,
  fetchUserFolders,
  getBusinessLogoUrl,
  moveBusinessesToFolder,
  removeBusinessesFromFolder,
  setFolderBusinessIndexes,
} from '../services/api';
import { displayFolderName } from '../utils/folderDisplayName';
import { useT } from '../i18n/useT';

export default function FolderDetailPage() {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [folderName, setFolderName] = useState('');
  const [businesses, setBusinesses] = useState<ApiBusiness[]>([]);
  const [organizeMode, setOrganizeMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [otherFolders, setOtherFolders] = useState<ApiFolder[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const folderId = useMemo(() => {
    if (!id) return null;
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }, [id]);

  const selectedCount = selectedIds.size;
  const selectedList = useMemo(() => Array.from(selectedIds), [selectedIds]);

  useEffect(() => {
    const load = async () => {
      if (!user?.token || folderId === null) {
        setError('Invalid list.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const [folderResponse, businessesResponse] = await Promise.all([
          fetchUserFolders(user.token, getApiLanguage()),
          fetchBusinessesFromFolder(folderId, user.token, getApiLanguage()),
        ]);

        const folders = folderResponse.folders || [];
        const folderIndex = folders.findIndex((item) => item.id === folderId);
        const folder = folderIndex >= 0 ? folders[folderIndex] : folders.find((item) => item.id === folderId);
        if (folder?.name) {
          setFolderName(displayFolderName(folder.name, folderIndex >= 0 ? folderIndex : 0));
        } else {
          setFolderName(t('folderListNumber', { n: folderId }));
        }
        setBusinesses(businessesResponse.businesses || []);
      } catch (err) {
        console.error('Failed to load folder details:', err);
        setError(t('folderLoadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [folderId, user?.token]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitOrganize = () => {
    setOrganizeMode(false);
    setSelectedIds(new Set());
    setDragIndex(null);
  };

  const reorderBusinesses = async (fromIndex: number, toIndex: number) => {
    if (!user?.token || folderId === null || fromIndex === toIndex) return;

    const previous = businesses;
    const next = [...businesses];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setBusinesses(next);
    setIsReordering(true);

    try {
      await setFolderBusinessIndexes(fromIndex, toIndex, folderId, user.token, getApiLanguage());
    } catch (err) {
      console.error(err);
      setBusinesses(previous);
      setError('Could not reorder this list right now.');
    } finally {
      setIsReordering(false);
      setDragIndex(null);
    }
  };

  const openMoveDialog = async () => {
    if (!user?.token || folderId === null || selectedCount === 0) return;
    try {
      const res = await fetchUserFolders(user.token, getApiLanguage());
      setOtherFolders((res.folders || []).filter((f) => f.id !== folderId));
      setMoveDialogOpen(true);
    } catch {
      setError('Could not load other lists.');
    }
  };

  const handleRemoveSelected = async () => {
    if (!user?.token || folderId === null || selectedCount === 0) return;
    setIsRemoving(true);
    try {
      await removeBusinessesFromFolder(selectedList, folderId, user.token, getApiLanguage());
      setBusinesses((current) => current.filter((item) => !selectedIds.has(item.id)));
      setRemoveDialogOpen(false);
      exitOrganize();
    } catch (err) {
      console.error(err);
      setError('Could not remove selected VISIs.');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleMoveTo = async (targetId: number) => {
    if (!user?.token || folderId === null || selectedCount === 0) return;
    setIsMoving(true);
    try {
      await moveBusinessesToFolder(
        selectedList,
        targetId,
        user.token,
        getApiLanguage(),
        folderId
      );
      setBusinesses((current) => current.filter((item) => !selectedIds.has(item.id)));
      setMoveDialogOpen(false);
      exitOrganize();
    } catch {
      setError('Could not move selected VISIs.');
    } finally {
      setIsMoving(false);
    }
  };

  const organizeActions = !isLoading ? (
    organizeMode ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        size="small"
        disabled={selectedCount === 0}
        onClick={() => void openMoveDialog()}
        aria-label={t('folderMoveSelectedAria')}
        sx={{ color: DesignSystem.colors.textPrimary }}
      >
        <DriveFileMove />
      </IconButton>
      <IconButton
        size="small"
        disabled={selectedCount === 0}
        onClick={() => setRemoveDialogOpen(true)}
        aria-label={t('folderRemoveSelectedAria')}
        sx={{ color: DesignSystem.colors.error }}
      >
        <DeleteOutline />
      </IconButton>
      <IconButton size="small" onClick={exitOrganize} aria-label={t('folderDoneOrganizingAria')}>
        <Close />
      </IconButton>
    </Box>
  ) : (
    <Button
      size="small"
      startIcon={<CheckBoxOutlineBlank sx={{ fontSize: 18 }} />}
      onClick={() => setOrganizeMode(true)}
      sx={{
        textTransform: 'none',
        color: DesignSystem.colors.primary,
        fontWeight: 650,
      }}
    >
      {t('folderOrganize')}
    </Button>
  )
  ) : undefined;

  return (
    <AppShell title={folderName} showBackButton maxWidth="feed" showCompanion actions={organizeActions}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {organizeMode && (
            <Typography sx={{ mb: 1.5, fontSize: 13, color: DesignSystem.colors.textSecondary }}>
              {isReordering
                ? t('folderSavingOrder')
                : selectedCount === 0
                  ? t('folderOrganizeDragHint')
                  : t('folderSelectedCount', { n: selectedCount })}
            </Typography>
          )}

          {!error && businesses.length === 0 && (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: DesignSystem.colors.textSecondary,
              }}
            >
              <Folder sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('folderEmptyTitle')}
              </Typography>
              <Typography variant="body2">{t('folderEmptyBody')}</Typography>
            </Box>
          )}

          {businesses.map((business, index) => {
            const selected = selectedIds.has(business.id);
            const isDragging = dragIndex === index;
            return (
              <Box
                key={business.id}
                draggable={organizeMode && !isReordering}
                onDragStart={(event) => {
                  if (!organizeMode) return;
                  event.dataTransfer.effectAllowed = 'move';
                  event.dataTransfer.setData('text/plain', String(index));
                  setDragIndex(index);
                }}
                onDragOver={(event) => {
                  if (!organizeMode || dragIndex === null) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(event) => {
                  if (!organizeMode) return;
                  event.preventDefault();
                  const fromIndex = Number(event.dataTransfer.getData('text/plain'));
                  if (!Number.isFinite(fromIndex) || fromIndex === index) {
                    setDragIndex(null);
                    return;
                  }
                  void reorderBusinesses(fromIndex, index);
                }}
                onDragEnd={() => setDragIndex(null)}
                onClick={() => {
                  if (organizeMode) {
                    toggleSelect(business.id);
                    return;
                  }
                  const username = business.username?.replace('@', '') || business.id.toString();
                  navigate(`/${username}`);
                }}
                sx={{
                  mb: 1.25,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  p: 1.5,
                  borderRadius: DesignSystem.borderRadius.md,
                  border: `1px solid ${
                    selected ? DesignSystem.colors.primary : DesignSystem.colors.separator
                  }`,
                  backgroundColor: selected
                    ? 'rgba(38,143,182,0.12)'
                    : DesignSystem.colors.surface,
                  cursor: organizeMode ? 'grab' : 'pointer',
                  opacity: isDragging ? 0.55 : 1,
                }}
              >
                {organizeMode && (
                  <IconButton
                    size="small"
                    draggable={false}
                    onClick={(event) => event.stopPropagation()}
                    aria-label={t('folderDragReorderAria')}
                    sx={{
                      p: 0.25,
                      cursor: 'grab',
                      color: DesignSystem.colors.textSecondary,
                      touchAction: 'none',
                    }}
                  >
                    <DragIndicator />
                  </IconButton>
                )}
                {organizeMode && (
                  <Checkbox
                    checked={selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(business.id)}
                    sx={{ p: 0.5, color: DesignSystem.colors.textSecondary }}
                  />
                )}
                <Avatar
                  src={business.logo ? getBusinessLogoUrl(business.logo) || undefined : undefined}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: DesignSystem.colors.surfaceElevated,
                  }}
                >
                  {business.name?.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: DesignSystem.colors.textPrimary,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {business.name || business.username}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: DesignSystem.colors.textSecondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {business.subcategory?.name || business.category?.name || t('noCategory')}
                  </Typography>
                  {business.addresses?.[0]?.city && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14, color: DesignSystem.colors.textSecondary }} />
                      <Typography variant="caption" sx={{ color: DesignSystem.colors.textSecondary }}>
                        {business.addresses[0].city}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </>
      )}

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>{t('folderRemoveFromListTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedCount === 1
              ? t('folderRemoveConfirm', { n: selectedCount })
              : t('folderRemoveConfirmMany', { n: selectedCount })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>{t('cancel')}</Button>
          <Button
            onClick={() => void handleRemoveSelected()}
            disabled={isRemoving}
            variant="contained"
            sx={{
              backgroundColor: DesignSystem.colors.error,
              '&:hover': { backgroundColor: '#d32f2f' },
            }}
          >
            {isRemoving ? t('removing') : t('remove')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('folderMoveToListTitle')}</DialogTitle>
        <DialogContent>
          {otherFolders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('folderNoOtherLists')}
            </Typography>
          ) : (
            otherFolders.map((folder, i) => (
              <Button
                key={folder.id}
                fullWidth
                sx={{ justifyContent: 'flex-start', mb: 1 }}
                disabled={isMoving}
                onClick={() => void handleMoveTo(folder.id)}
              >
                {displayFolderName(folder.name, i)}
              </Button>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
